/**
 * The spinner that stands in front of everything until there is a world.
 *
 * It lives in `index.html` rather than being built here, and that is the whole
 * reason it exists in two places: something has to be on the screen before any
 * of this code has been parsed, and an element created by a module cannot be.
 * What this file adds is the **rest of its life** — the caption for whichever
 * phase is being waited on, and being taken down at the right moment rather
 * than at the first convenient one.
 *
 * Its phases, in order: the session, the place to stand, and then the world
 * itself arriving out of the fog. The last one is the long one and it used to
 * be the only one nothing was said during — the loader came down as the renderer
 * started, which is exactly when the fog closes, so the player was left looking
 * at weather with no word about what was happening behind it.
 */

const CAPTION_ID = 'loader-caption';

function element(): HTMLElement | null {
  return document.getElementById('loader');
}

/**
 * Put it back up, with a word about what is being waited for.
 *
 * Idempotent, and it has to be: the login screen takes the loader down, and
 * everything after a successful login puts it back.
 */
export function showLoader(caption?: string): void {
  const loader = element();
  if (!loader) return;

  loader.classList.remove('hidden', 'fading');
  if (caption !== undefined) {
    const line = document.getElementById(CAPTION_ID);
    if (line) line.textContent = caption;
  }
}

/**
 * Take it down.
 *
 * Faded rather than cut, because the moment it goes is the moment the world is
 * becoming visible behind it — and a spinner that vanishes on one frame reads
 * as a glitch in something that is otherwise a slow reveal. The element is
 * hidden outright once the fade is over, so it stops being in the way of the
 * cursor even in a browser that ignores `pointer-events`.
 */
export function hideLoader(): void {
  const loader = element();
  if (!loader || loader.classList.contains('hidden')) return;

  loader.classList.add('fading');
  window.setTimeout(() => loader.classList.add('hidden'), FADE_MS);
}

/** Matches the transition in `index.html`. */
const FADE_MS = 600;
